/**
 * 모델 mesh 명명 규칙 파서 (three 비의존 — 순수 문자열 로직).
 *
 * 명명 규칙: `{BuildingId}_{Floor}_{Kind}` (예: BDG_101_F01_WALL, BDG_MXD_BF1_SLAB)
 *   - BuildingId: `BDG_XXX` (숫자 BDG_101 / 문자 BDG_MXD 모두 허용, XXX는 건물 고유값)
 *   - Floor:      F01~F99 / BF1~BF9(지하) / B1~B9 / PH·PH1·PH2(옥탑·펜트하우스) / RF
 *   - Kind:       WALL / SLAB / ROOF / COLUMN / BEAM / FACADE / STAIR / WINDOW / DOOR
 *                 / SAFETY_NET(비계) / GANGFORM(갱폼) / SHORE(동바리)
 *
 * 예외:
 *   - TMP_*    : 임시구조물 (층 무관)
 *   - GROUND_* : 지면 (층 무관). 대소문자 무시 — 모델이 "Ground"로 명명하기도 함.
 *
 * 명명을 따르지 않는 mesh는 `kind: "OTHER"`로 분류.
 */

export type FloorKey = string; // "F01" | "B1" | "PH1" | "RF" | "OUTSIDE"
export type MeshKind =
  | "WALL"
  | "WALL_EXT"
  | "WALL_INT"
  | "SLAB"
  | "ROOF"
  | "COLUMN"
  | "BEAM"
  | "FACADE"
  | "STAIR"
  | "WINDOW"
  | "DOOR"
  | "SAFETY_NET"
  | "GANGFORM"
  | "SHORE"
  | "TMP"
  | "GROUND"
  | "OTHER";

export interface MeshLayerInfo {
  buildingId: string | null;
  floor: FloorKey | null;
  kind: MeshKind;
}

// - BuildingId는 숫자(BDG_101)·문자(BDG_MXD) 모두 허용
// - floor 부분은 옵션 — `BDG_101_ROOF` 처럼 층 없이 명명되는 경우도 매칭
// - BF\d 는 지하층(basement). B\d 보다 먼저 와야 BF1이 B로 오인되지 않음
// - PH\d? : 옥탑/펜트하우스. PH(단독)·PH1·PH2 모두 허용 (모델이 PH1/PH2로 층 구분)
const NAME_REGEX =
  /^(BDG_[A-Za-z0-9]+)(?:_(F\d{1,2}|BF\d{1,2}|B\d{1,2}|PH\d?|RF))?_([A-Z_]+)(?:_.+)?$/;

/** F1 ↔ F01, BF1 → B1(지하) 같은 표기 불일치 정규화. PH/PH1/PH2·RF는 그대로 유지. */
export function normalizeFloor(raw: string): FloorKey {
  if (raw === "RF") return raw;
  if (raw.startsWith("PH")) return raw; // PH / PH1 / PH2 — 별개 층으로 보존
  if (raw.startsWith("BF")) {
    // 지하 BF1 → B1 (floorOrder/floorLabel의 B 분기 재사용)
    const n = parseInt(raw.slice(2), 10);
    return Number.isFinite(n) ? `B${n}` : raw;
  }
  if (raw.startsWith("F")) {
    const n = parseInt(raw.slice(1), 10);
    return Number.isFinite(n) ? `F${String(n).padStart(2, "0")}` : raw;
  }
  if (raw.startsWith("B")) {
    const n = parseInt(raw.slice(1), 10);
    return Number.isFinite(n) ? `B${n}` : raw;
  }
  return raw;
}

function normalizeKind(raw: string): MeshKind {
  // WALL_EXT / WALL_INT 구분 보존
  if (raw === "WALL_EXT" || raw === "WALL_INT") return raw;
  // 비계/안전망 (SAFETY_NET) — 콘크리트와 겹쳐 z-fighting 유발, 토글 대상
  if (raw.startsWith("SAFETY")) return "SAFETY_NET";
  // 첫 토큰만으로 분류 (예: SLAB_HEAVY → SLAB)
  const head = raw.split("_")[0] ?? "";
  const known: MeshKind[] = [
    "WALL",
    "SLAB",
    "ROOF",
    "COLUMN",
    "BEAM",
    "FACADE",
    "STAIR",
    "WINDOW",
    "DOOR",
    "GANGFORM",
    "SHORE",
  ];
  return (known.includes(head as MeshKind) ? head : "OTHER") as MeshKind;
}

/** mesh 이름 → 계층 정보 파싱 (단일 이름) */
export function parseMeshName(name: string): MeshLayerInfo {
  if (!name) return { buildingId: null, floor: null, kind: "OTHER" };
  // TMP/GROUND 는 대소문자 무시 (모델이 "Ground" 등으로 명명하기도 함)
  const upper = name.toUpperCase();
  if (upper.startsWith("TMP")) return { buildingId: null, floor: null, kind: "TMP" };
  if (upper.startsWith("GROUND")) return { buildingId: null, floor: null, kind: "GROUND" };

  const m = NAME_REGEX.exec(name);
  if (!m) return { buildingId: null, floor: null, kind: "OTHER" };
  const [, buildingId, floor, kindRaw] = m;
  return {
    buildingId: buildingId ?? null,
    floor: floor ? normalizeFloor(floor) : null,
    kind: normalizeKind(kindRaw ?? "OTHER"),
  };
}

/** FloorKey → 정렬용 숫자 (B1=-1, F01=1, F02=2, PH=90, PH1=91, PH2=92, RF=100) */
export function floorOrder(key: FloorKey): number {
  if (key === "RF") return 100;
  if (key.startsWith("PH")) return 90 + (parseInt(key.slice(2), 10) || 0);
  if (key.startsWith("F")) return parseInt(key.slice(1), 10) || 0;
  if (key.startsWith("B")) return -(parseInt(key.slice(1), 10) || 0);
  return 0;
}

/** FloorKey → 표시 라벨 (F01 → "1F", PH1 → "PH1", B1 → "B1") */
export function floorLabel(key: FloorKey): string {
  if (key === "RF") return "RF";
  if (key.startsWith("PH")) return key; // "PH" / "PH1" / "PH2"
  if (key.startsWith("F")) {
    const n = parseInt(key.slice(1), 10);
    return Number.isFinite(n) ? `${n}F` : key;
  }
  return key;
}
